import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { alpha, IconButton, InputAdornment, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Iconify } from '../../Iconify';

const DEBOUNCE_MS = 300;

interface TextFilterProps {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  // Compact search box for the pill row: placeholder instead of a label, and
  // collapsed to a search icon while empty so it takes no room.
  inline?: boolean;
}

// Local draft so typing feels responsive while the URL write is debounced.
export const TextFilter: FC<TextFilterProps> = ({
  label,
  value,
  onChange,
  inline,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value ?? '');
  // Opened by a click on the icon; a set value keeps it open regardless.
  const [expanded, setExpanded] = useState(false);

  // Follow outside changes: the chip's unset, back button, deep link.
  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  useEffect(() => {
    const trimmed = draft.trim();
    if (trimmed === (value ?? '')) {
      return undefined;
    }
    const handle = setTimeout(
      () => onChange(trimmed === '' ? undefined : trimmed),
      DEBOUNCE_MS
    );
    return () => clearTimeout(handle);
  }, [draft, value, onChange]);

  if (inline) {
    const set = draft.trim() !== '';
    if (!set && !expanded) {
      return (
        <IconButton
          aria-label={label}
          onClick={() => setExpanded(true)}
          // Same size and outline as the filter chips next to it.
          sx={(theme) => ({
            width: 32,
            height: 32,
            border: `1px solid ${theme.palette.grey[500_32]}`,
            bgcolor: 'background.paper',
            color: 'text.secondary',
          })}
        >
          <Iconify icon="eva:search-fill" width={18} />
        </IconButton>
      );
    }
    return (
      <TextField
        placeholder={label}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        // Only when opened by a click, not when a deep link sets the value.
        autoFocus={expanded}
        // Keeps a deep-linked search open while its text is deleted.
        onFocus={() => setExpanded(true)}
        onBlur={() => {
          if (draft.trim() === '') {
            setExpanded(false);
          }
        }}
        size="small"
        // Styled like the outlined filter chips next to it.
        sx={(theme) => ({
          width: 240,
          '& .MuiOutlinedInput-root': {
            height: 32,
            borderRadius: 16,
            pl: 1,
            pr: 0.5,
            fontSize: theme.typography.pxToRem(13),
            color: set ? 'primary.main' : 'text.primary',
            bgcolor: set
              ? alpha(theme.palette.primary.main, 0.08)
              : 'background.paper',
            '& fieldset': {
              borderColor: set
                ? theme.palette.primary.main
                : theme.palette.grey[500_32],
            },
          },
          '& .MuiOutlinedInput-input': { py: 0, pl: 0.5 },
        })}
        slotProps={{
          htmlInput: { 'aria-label': label },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Iconify
                  icon="eva:search-fill"
                  sx={{
                    color: set ? 'primary.main' : 'text.disabled',
                    width: 18,
                    height: 18,
                    // Nunito renders high in its line box; the icon is
                    // geometrically centered, so it reads as too low.
                    mt: '-2px',
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: set ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={t('common.clear-all')}
                  onClick={() => {
                    setDraft('');
                    setExpanded(false);
                  }}
                  sx={{ p: 0.25 }}
                >
                  <Iconify icon="eva:close-circle-fill" width={18} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        }}
      />
    );
  }

  return (
    <TextField
      label={label}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      size="small"
      fullWidth
    />
  );
};
